import type DialogComponent from 'components/dialog-new';
import { nanoid } from 'nanoid';
import { defineStore } from 'pinia';
import type {
	Component,
	ComponentInstance,
	HTMLAttributes,
	MaybeRef,
} from 'vue';
import type { Cons } from 'vue-facing-decorator/dist/component';

export interface Dialog extends DialogBase {
	header?: DialogContent;
	body: DialogContent;
	footer?: DialogContent;
}

export interface DialogWithId extends Dialog {
	id: string;
}

interface DialogBase {
	props?: DialogValue<ComponentInstance<typeof DialogComponent>['$props']>;
}

export type DialogContent = DialogValue<DialogSlotSource[] | DialogSlotSource>;

export interface DialogSlot<T extends DialogTag = DialogTag> {
	tag?: DialogValue<T>;
	props?: DialogValue<DialogSlotProps<T>>;
	slot?: DialogValue<DialogSlotSource[] | DialogSlotSource | string>;
}

export type DialogSlotProps<T extends DialogTag> = (
	(
		T extends Cons
			? LegacyDialogSlotProps<T>
			: T extends Component
				? ComponentInstance<T>['$props']
				// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				: {}
	)
	& HTMLAttributes
);

export type DialogSlotSource = DialogValue<DialogSlot>;

export type DialogTag = string | Component | Cons;

export type DialogValue<T> = MaybeRef<T>;

type LegacyDialogSlotProps<T extends Cons> = (
	PublicNonVueOptionalNonFunctionProps<InstanceType<T>>
	& {
		[K in keyof PublicNonVueOptionalNonFunctionProps<InstanceType<T>> & string as `onUpdate:${K}`]?: (value: NonNullable<PublicNonVueOptionalNonFunctionProps<InstanceType<T>>[K]>) => any;
	}
	& {
		[K in `on${string}`]?: (...args: any[]) => any;
	}
);

interface UIState {
	dialogs: DialogWithId[];
}

interface UIActions {
	openDialog(dialog: Dialog): () => void;
	closeDialog(): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
const useUIStore = defineStore<'ui', UIState, {}, UIActions>(
	'ui',
	{
		state: () => ({
			dialogs: [],
		}),
		actions: {
			openDialog(dialog) {
				const typedDialog = dialog as typeof this.dialogs[number];
				typedDialog.id = nanoid();
				this.dialogs.push(typedDialog);

				return () => {
					const index = this.dialogs.indexOf(typedDialog);

					if (index !== -1) {
						this.dialogs.splice(
							index,
							1,
						);
					}
				};
			},
			closeDialog() {
				this.dialogs.pop();
			},
		},
	},
);

function dialogSlot<T extends DialogTag = string>(slot: DialogSlot<T>): DialogSlot<T> {
	return slot;
}

export {
	dialogSlot,
	useUIStore,
};
